import { useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { FaCheck, FaExclamation, FaTimes, FaStop } from "react-icons/fa";
import { getTaskStatus } from '../services/uploadService';
import axios from 'axios';
import { toast } from 'react-toastify';

const PHASES = [
  { name: 'Preparing', icon: '1' },
  { name: 'SourceOptima AI', icon: '2' },
  { name: 'SourceOptima Database', icon: '3' },
  { name: 'Complete', icon: '4' },
]

// SourceOptima Corporate Colors
const COLORS = {
  deepTeal: '#1e5045',
  cream: '#f6f4ed',
  orange: '#f26522',
  white: '#fff',
  accentGreen: '#94c43d',
}

const LEVEL_COLORS = {
  phase: 'text-[#f26522] font-bold',      // Orange for phase headers
  success: 'text-[#94c43d]',               // Accent green for success
  info: 'text-gray-400',                   // Gray for info
  warning: 'text-[#f26522]',               // Orange for warnings
  error: 'text-red-400',                   // Red for errors
}

export default function AnalysisProgressModal({
  taskId,
  playName,
  fileCount,
  onComplete,
  onError,
  onClose,
  onStop,
  // Navigation props for assembly analyses
  folderName,
  spacePath,
  fileIds,
}) {
  const history = useHistory()
  const [progress, setProgress] = useState(null)
  const [status, setStatus] = useState('PENDING')
  const [isStopping, setIsStopping] = useState(false)
  const [completedResult, setCompletedResult] = useState(null)
  const [startTime] = useState(Date.now())
  const [logs, setLogs] = useState([
    { timestamp: 0, level: 'info', message: 'Initializing analysis...' }
  ])
  const logContainerRef = useRef(null)
  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL

  // Dynamic polling backoff state
  const [pollInterval, setPollInterval] = useState(500)
  const [concurrentUsers, setConcurrentUsers] = useState(0)
  const [isThrottled, setIsThrottled] = useState(false)

  // Drawing checker play types that have full HTML reports
  const DRAWING_CHECKER_PLAYS = [
    'veeco_drawing_checker_v1',
    'veeco_drawing_checker_v2',
    'veeco_drawing_checker_veeco_v1',
    'veeco_drawing_checker_general_v1',
  ]

  // Open the full HTML report in a new tab for drawing checker results
  const openHtmlReport = async (result) => {
    const playId = result?.play_id
    const firstSuccess = result?.results?.find(r => r.success)
    if (!playId || !firstSuccess?.file_id) {
      // Fallback to default behavior
      onComplete?.(result)
      return
    }

    const token = sessionStorage.getItem("userToken")
    const reportUrl = `${BASE_URL}/ocr/export/drawing-checker/${firstSuccess.file_id}/?play_type=${playId}`

    const newWindow = window.open('about:blank', '_blank')
    if (!newWindow) {
      toast.error('Please allow popups for this site to view the report')
      onComplete?.(result)
      return
    }

    newWindow.document.write('<html><body><h2>Loading report...</h2></body></html>')

    try {
      const response = await axios.get(reportUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'
      })

      let htmlContent = response.data
      htmlContent = htmlContent.replace(/href=["']\/ocr\//g, `href="${BASE_URL}/ocr/`)
      htmlContent = htmlContent.replace(/src=["']\/ocr\//g, `src="${BASE_URL}/ocr/`)

      // Inject auth script for download links & feedback buttons
      const authScript = `
        <script>
          (function() {
            var token = '${token}';
            var baseUrl = '${BASE_URL}';
            var playType = '${playId}';
            window.__authToken = token;
            window.__baseUrl = baseUrl;
            document.addEventListener('click', function(e) {
              var link = e.target.closest('a[href*="/ocr/download/"]');
              if (link) {
                e.preventDefault();
                var url = link.href;
                if (!url.includes('play_type=')) {
                  url += (url.includes('?') ? '&' : '?') + 'play_type=' + encodeURIComponent(playType);
                }
                fetch(url, { headers: { 'Authorization': 'Bearer ' + token } })
                  .then(function(r) { if (!r.ok) throw new Error('Download failed'); return r.blob().then(function(b) { return { blob: b, filename: 'Drawing_Checker_Report.pdf' }; }); })
                  .then(function(d) { var a = document.createElement('a'); a.href = URL.createObjectURL(d.blob); a.download = d.filename; a.click(); URL.revokeObjectURL(a.href); })
                  .catch(function(err) { alert('Download failed: ' + err.message); });
              }
            });
          })();
        </script>
      `
      htmlContent = htmlContent.replace('</body>', authScript + '</body>')

      newWindow.document.open()
      newWindow.document.write(htmlContent)
      newWindow.document.close()
    } catch (err) {
      console.error('Failed to load HTML report:', err)
      newWindow.close()
      toast.error('Failed to load report. Opening file card instead.')
    }

    // Always clean up the modal
    onComplete?.(result)
  }

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [logs, progress?.status_log])

  // Add initial log entry on mount
  useEffect(() => {
    const elapsed = ((Date.now() - startTime) / 1000).toFixed(1)
    setLogs(prev => [
      ...prev,
      { timestamp: parseFloat(elapsed), level: 'success', message: `Starting ${playName} for ${fileCount} file(s)` },
      { timestamp: parseFloat(elapsed) + 0.1, level: 'info', message: 'Connecting to SourceOptima AI...' },
    ])
  }, [])

  // Handle stop/cancel
  const handleStop = async () => {
    if (isStopping) return
    setIsStopping(true)

    const token = sessionStorage.getItem("userToken")
    const elapsed = ((Date.now() - startTime) / 1000).toFixed(1)

    setLogs(prev => [
      ...prev,
      { timestamp: parseFloat(elapsed), level: 'warning', message: '[STOP] Cancellation requested...' }
    ])

    try {
      await axios.post(
        `${BASE_URL}/ocr/cancel-task/${taskId}/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setStatus('CANCELLED')
      setLogs(prev => [
        ...prev,
        { timestamp: parseFloat(((Date.now() - startTime) / 1000).toFixed(1)), level: 'warning', message: '[STOPPED] Analysis stopped. Cached results preserved.' }
      ])

      onStop?.()
    } catch (err) {
      console.error('Failed to cancel task:', err)
      setLogs(prev => [
        ...prev,
        { timestamp: parseFloat(((Date.now() - startTime) / 1000).toFixed(1)), level: 'error', message: '[ERROR] Failed to stop analysis' }
      ])
      setIsStopping(false)
    }
  }

  useEffect(() => {
    if (!taskId) return

    let isMounted = true
    let pollCount = 0
    const MAX_POLLS = 1200 // 10 minutes at 500ms
    const token = sessionStorage.getItem("userToken");
    let currentPollInterval = 500 // Start with default, will be updated dynamically
    let timeoutId = null

    const pollStatus = async () => {
      if (!isMounted) return

      try {
        pollCount++
        console.log(`[AnalysisProgress] Polling task ${taskId} (attempt ${pollCount}, interval ${currentPollInterval}ms)`)

        const data = await getTaskStatus(taskId, token)

        if (!isMounted) return

        // Update dynamic polling backoff state
        if (data.recommended_poll_interval_ms) {
          currentPollInterval = data.recommended_poll_interval_ms
          setPollInterval(data.recommended_poll_interval_ms)
        }
        if (data.concurrent_users !== undefined) {
          setConcurrentUsers(data.concurrent_users)
        }
        if (data.throttled !== undefined) {
          setIsThrottled(data.throttled)
        }

        console.log(`[AnalysisProgress] Task status: ${data.status}`, data)
        setStatus(data.status)

        if (data.status === 'PROGRESS' && data.progress) {
          setProgress(data.progress)

          // Update logs from backend
          if (data.progress.status_log) {
            setLogs(data.progress.status_log)
          }
        } else if (data.status === 'SUCCESS') {
          console.log('[AnalysisProgress] Task completed successfully')
          console.log('[AnalysisProgress] Full result data:', data.result)
          console.log('[AnalysisProgress] assembly_id in result:', data.result?.assembly_id)

          // Final logs
          if (data.result?.status_log) {
            setLogs(data.result.status_log)
          }

          setProgress(prev => ({
            ...prev,
            stage: 'complete',
            phase: 3,
            progress_pct: 100,
          }))

          // Handle assembly analysis navigation directly from modal
          const assemblyId = data.result?.assembly_id
          if (assemblyId) {
            console.log('[AnalysisProgress] Assembly analysis detected, navigating directly to BOM approval:', assemblyId)
            setTimeout(() => {
              toast.success("Assembly analysis complete! Review the BOM structure...")
              history.push({
                pathname: `/bom-approval/${assemblyId}`,
                state: {
                  folderName: folderName,
                  spacePath: spacePath,
                  fileIds: fileIds || []
                }
              })
              // Still call onComplete for cleanup, but navigation already happened
              onComplete?.(data.result)
            }, 1500)
          } else {
            // For individual analysis, store result and let user close manually
            console.log('[AnalysisProgress] Calling onComplete with:', data.result)
            setCompletedResult(data.result)
          }
          return // Stop polling
        } else if (data.status === 'FAILURE') {
          console.error('[AnalysisProgress] Task failed:', data.error)
          onError?.(data.error || 'Analysis failed')
          return // Stop polling
        } else if (pollCount >= MAX_POLLS) {
          console.error('[AnalysisProgress] Polling timeout')
          onError?.('Analysis timeout - please check server logs')
          return // Stop polling
        }

        // Schedule next poll with dynamic interval
        timeoutId = setTimeout(pollStatus, currentPollInterval)
      } catch (err) {
        console.error('[AnalysisProgress] Error polling status:', err)
        if (pollCount >= 10) {
          onError?.('Failed to connect to server')
          return // Stop polling
        }
        // Retry with current interval on error
        timeoutId = setTimeout(pollStatus, currentPollInterval)
      }
    }

    pollStatus()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [taskId, onComplete, onError])

  const currentPhase = progress?.phase ?? 0
  const elapsedSeconds = progress?.elapsed_seconds ??
    Math.round((Date.now() - startTime) / 1000)

  const displayLogs = progress?.status_log || logs

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f6f4ed] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-[#1e5045]/20">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e5045]/10 bg-white">
          <div>
            <h2 className="text-xl font-semibold text-[#1e5045]">
              {status === 'CANCELLED' ? 'Analysis Stopped' : `Running ${playName}`}
            </h2>
            <p className="text-sm text-[#1e5045]/60">{fileCount} file(s) selected</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Stop Button - show when running */}
            {status !== 'SUCCESS' && status !== 'FAILURE' && status !== 'CANCELLED' && (
              <button
                onClick={handleStop}
                disabled={isStopping}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isStopping
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                }`}
              >
                <FaStop className="w-3 h-3" />
                {isStopping ? 'Stopping...' : 'Stop'}
              </button>
            )}
            {/* View Results button - show when individual analysis is done */}
            {completedResult && (
              <button
                onClick={() => {
                  if (DRAWING_CHECKER_PLAYS.includes(completedResult?.play_id)) {
                    openHtmlReport(completedResult)
                  } else {
                    onComplete?.(completedResult)
                  }
                }}
                className="px-4 py-2 bg-[#1e5045] text-white rounded-lg text-sm font-medium hover:bg-[#0d5d57] transition-colors"
              >
                View Results
              </button>
            )}
            {/* Close Button - show when finished or cancelled */}
            {(status === 'SUCCESS' || status === 'FAILURE' || status === 'CANCELLED') && (
              <button
                onClick={completedResult ? () => {
                  if (DRAWING_CHECKER_PLAYS.includes(completedResult?.play_id)) {
                    openHtmlReport(completedResult)
                  } else {
                    onComplete?.(completedResult)
                  }
                } : onClose}
                className="p-2 hover:bg-[#1e5045]/10 rounded-lg transition-colors"
              >
                <FaTimes className="w-4 h-4 text-[#1e5045]" />
              </button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Phase Stepper */}
          <div className="flex flex-wrap gap-2">
            {PHASES.map((phase, idx) => {
              let bgColor = 'bg-[#1e5045]/10 text-[#1e5045]/50'
              let iconBg = 'bg-[#1e5045]/20'
              if (idx < currentPhase) {
                bgColor = 'bg-[#94c43d] text-white'
                iconBg = 'bg-white/30'
              } else if (idx === currentPhase) {
                bgColor = 'bg-[#f26522] text-white animate-pulse'
                iconBg = 'bg-white/20'
              }

              return (
                <div
                  key={phase.name}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${bgColor}`}
                >
                  <span className={`w-4 h-4 rounded-full ${iconBg} flex items-center justify-center text-[10px]`}>
                    {idx < currentPhase ? '✓' : phase.icon}
                  </span>
                  {phase.name}
                </div>
              )
            })}
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#1e5045]/70 truncate max-w-[70%]">
                {progress?.message || 'Preparing analysis...'}
              </span>
              <span className="font-medium text-[#f26522]">
                {typeof elapsedSeconds === 'number' ? elapsedSeconds.toFixed(1) : elapsedSeconds}s
              </span>
            </div>
            <div className="h-2 bg-[#1e5045]/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#f26522] to-[#94c43d] rounded-full transition-all duration-300"
                style={{ width: `${progress?.progress_pct || 5}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-[#1e5045]/50">
              <span>
                {progress?.current !== undefined && progress?.total !== undefined
                  ? `${progress.current} / ${progress.total} files`
                  : ''}
              </span>
              <span>{Math.round(progress?.progress_pct || 0)}%</span>
            </div>
          </div>

          {/* Activity Log - Terminal Style */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-[#1e5045]">Activity Log</h4>
              <span className="text-xs text-[#1e5045]/40">
                {displayLogs.length} entries
              </span>
            </div>
            <div
              ref={logContainerRef}
              className="bg-[#1e5045] rounded-lg p-3 h-64 overflow-y-auto font-mono text-xs"
            >
              {displayLogs.length > 0 ? (
                displayLogs.map((entry, idx) => (
                  <div
                    key={idx}
                    className={`py-0.5 ${LEVEL_COLORS[entry.level] || 'text-gray-400'} ${
                      entry.level === 'phase' ? 'mt-2 first:mt-0' : ''
                    }`}
                  >
                    <span className="text-[#f6f4ed]/40 mr-2">
                      [{typeof entry.timestamp === 'number'
                        ? entry.timestamp.toFixed(1).padStart(6)
                        : '  0.0'}s]
                    </span>
                    {entry.message}
                  </div>
                ))
              ) : (
                <div className="text-[#f6f4ed]/50 flex items-center gap-2">
                  <span className="animate-pulse">●</span>
                  Waiting for analysis to start...
                </div>
              )}
              {/* Throttle notification */}
              {isThrottled && status !== 'SUCCESS' && status !== 'FAILURE' && (
                <div className="py-0.5 text-[#f26522] mt-2">
                  <span className="text-[#f6f4ed]/40 mr-2">[SYSTEM]</span>
                  High server activity ({concurrentUsers} users) - updates slowed to every {(pollInterval / 1000).toFixed(1)}s
                </div>
              )}
              {/* Blinking cursor */}
              {status !== 'SUCCESS' && status !== 'FAILURE' && (
                <div className="text-[#94c43d] animate-pulse mt-1">▌</div>
              )}
            </div>
          </div>

          {/* Status Indicator */}
          <div className="flex items-center gap-2 text-sm">
            {status === 'SUCCESS' ? (
              <>
                <FaCheck className="w-5 h-5 text-[#94c43d]" />
                <span className="text-[#1e5045] font-medium">
                  Analysis complete! Click <strong>View Results</strong> to continue.
                </span>
              </>
            ) : status === 'FAILURE' ? (
              <>
                <FaExclamation className="w-5 h-5 text-red-500" />
                <span className="text-red-600 font-medium">Analysis failed</span>
              </>
            ) : (
              <>
                <div className="w-5 h-5 border-2 border-[#f26522] border-t-transparent rounded-full animate-spin" />
                <span className="text-[#1e5045]">
                  Running analysis via SourceOptima AI...
                  {progress?.successful !== undefined && (
                    <span className="ml-2 text-[#1e5045]/50">
                      ({progress.successful} successful
                      {progress.cached > 0 && `, ${progress.cached} cached`}
                      {progress.failed > 0 && `, ${progress.failed} failed`})
                    </span>
                  )}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
